import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { type LunarDay, ZODIAC_SIGNS } from '@yadviga/shared'
import { api } from '../../api/client'
import { useSession } from '../../store/user.store'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { PageHeader } from '../../components/ui/PageHeader'
import { Modal } from '../../components/ui/Modal'
import { MoonPhaseIcon } from '../../components/luna/MoonPhaseIcon'

/**
 * Traditional month calendar — 7 columns × N rows (4-6 weeks), aligned
 * by day-of-week. Today is highlighted with a copper ring; days that
 * are auspicious for any activity get a subtle sage tint. Tap a cell
 * to open a bottom-sheet with the full breakdown.
 *
 * Calendar week starts on Monday (Russian/EU convention).
 */
export function LunaMonthRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const lang = (user?.language ?? 'ru') as 'ru' | 'en'
  const [openDay, setOpenDay] = useState<LunarDay | null>(null)
  // Cursor for which month is displayed. Local-time month, since users
  // think in their local calendar — server still does its math in tz.
  const [cursor, setCursor] = useState<{ year: number; month0: number }>(() => {
    const now = new Date()
    return { year: now.getFullYear(), month0: now.getMonth() }
  })

  const monthStart = new Date(cursor.year, cursor.month0, 1)
  const monthEnd = new Date(cursor.year, cursor.month0 + 1, 0)
  const daysInMonth = monthEnd.getDate()
  const fromIso = isoLocalDate(monthStart)

  const query = useQuery({
    queryKey: ['lunar', 'month', fromIso, daysInMonth],
    queryFn: () =>
      api.lunarMonth({ from: fromIso, days: daysInMonth, tz: tzGuess() }),
    staleTime: 1000 * 60 * 60 * 6,
  })

  const grid = useMemo(() => buildMonthGrid(cursor.year, cursor.month0, query.data ?? []), [
    cursor.year,
    cursor.month0,
    query.data,
  ])

  const todayIso = isoLocalDate(new Date())
  const monthLabel = new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(monthStart)

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('lunar.month_title')}
        subtitle={t('lunar.month_desc')}
        backTo="/luna"
      />

      {/* Month nav */}
      <div className="flex items-center justify-between rounded-2xl bg-white/70 p-2 ring-1 ring-sage-200/60">
        <button
          type="button"
          onClick={() => setCursor((c) => stepMonth(c, -1))}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-sage-700 hover:bg-sage-100 active:scale-95"
          aria-label={t('lunar.prev_month')}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="font-serif text-base capitalize text-sage-800">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setCursor((c) => stepMonth(c, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-sage-700 hover:bg-sage-100 active:scale-95"
          aria-label={t('lunar.next_month')}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {query.isLoading && <LoadingDots compact />}
      {query.isError && <ErrorState onRetry={() => query.refetch()} />}

      {query.data && (
        <>
          {/* Weekday header — пн вт ср чт пт сб вс */}
          <div className="grid grid-cols-7 gap-1 px-1 text-center text-[10px] uppercase tracking-wider text-sage-500">
            {weekdayLabels(lang).map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-7 gap-1.5">
            {grid.map((cell, i) => {
              if (cell.kind === 'pad') {
                return <div key={`pad-${i}`} className="aspect-square" aria-hidden />
              }
              const isToday = cell.day.date === todayIso
              const isAuspicious = cell.day.auspiciousFor.length > 0
              return (
                <button
                  key={cell.day.date}
                  onClick={() => setOpenDay(cell.day)}
                  className={
                    'flex aspect-square flex-col items-center justify-center gap-0.5 rounded-2xl p-1 ring-1 transition active:scale-[0.97] ' +
                    (isToday
                      ? 'bg-[#fbeadd] ring-copper'
                      : isAuspicious
                        ? 'bg-sage-100/80 ring-sage-300/60'
                        : 'bg-white/60 ring-sage-200/40')
                  }
                >
                  <span
                    className={
                      'text-[11px] font-medium ' +
                      (isToday ? 'text-copper' : 'text-sage-600')
                    }
                  >
                    {dateNumber(cell.day.date)}
                  </span>
                  <MoonPhaseIcon
                    phase={cell.day.phase}
                    className={'h-5 w-5 ' + (isToday ? 'text-copper' : 'text-sage-700')}
                  />
                  <span className="text-[9px] text-sage-500">
                    {Math.round(cell.day.illumination * 100)}%
                  </span>
                </button>
              )
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 px-1 text-[11px] text-sage-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-md bg-[#fbeadd] ring-1 ring-copper" />
              {t('lunar.legend_today')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-md bg-sage-100/80 ring-1 ring-sage-300/60" />
              {t('lunar.legend_auspicious')}
            </span>
          </div>
        </>
      )}

      <Modal
        open={!!openDay}
        onClose={() => setOpenDay(null)}
        title={openDay ? longDate(openDay.date, lang) : ''}
      >
        {openDay && <DayDetail day={openDay} lang={lang} />}
      </Modal>
    </div>
  )
}

function DayDetail({ day, lang }: { day: LunarDay; lang: 'ru' | 'en' }) {
  const { t } = useTranslation()
  const sign = ZODIAC_SIGNS.find((s) => s.id === day.moonSign)
  const signLabel = sign ? (lang === 'en' ? sign.nameEn : sign.nameRu) : day.moonSign
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <MoonPhaseIcon phase={day.phase} className="h-10 w-10 text-sage-700" />
        <div>
          <p className="font-serif text-lg text-sage-800">{t(`lunar.phase.${day.phase}` as never)}</p>
          <p className="text-xs text-sage-500">
            {t('home.lunar_illumination', { percent: Math.round(day.illumination * 100) })}
          </p>
        </div>
      </div>

      <ul className="flex flex-col gap-1 text-sm text-sage-700">
        <li>
          <span className="text-sage-500">{t('lunar.lunar_day')}:</span>{' '}
          <span className="text-sage-800">{day.lunarDay}</span>
        </li>
        <li>
          <span className="text-sage-500">{t('lunar.moon_sign')}:</span>{' '}
          <span className="text-sage-800">{signLabel}</span>
        </li>
        {day.voidOfCourse && (
          <li className="rounded-lg bg-[#fbeadd] px-2 py-1 text-xs text-copper">
            {t('lunar.void_of_course')}
          </li>
        )}
      </ul>

      <p className="leading-relaxed text-sage-800">{day.energyHint[lang]}</p>

      {day.auspiciousFor.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs uppercase tracking-wider text-sage-500">{t('lunar.good_for')}</p>
          <div className="flex flex-wrap gap-1.5">
            {day.auspiciousFor.map((a) => (
              <span key={a} className="rounded-full bg-sage-100 px-2.5 py-1 text-xs text-sage-700">
                {t(`lunar.activity.${a}` as never)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------- helpers ----------

type GridCell = { kind: 'pad' } | { kind: 'day'; day: LunarDay }

/**
 * Build a 7-col grid covering the full visible month. Pads the first
 * row with empty cells so the 1st of the month sits under the correct
 * weekday column, and pads the last row to a multiple of 7.
 */
function buildMonthGrid(year: number, month0: number, days: LunarDay[]): GridCell[] {
  const monthStart = new Date(year, month0, 1)
  // JS getDay: 0=Sun..6=Sat. We want Monday-first: shift so Mon=0, Sun=6.
  const padBefore = (monthStart.getDay() + 6) % 7
  const cells: GridCell[] = []
  for (let i = 0; i < padBefore; i++) cells.push({ kind: 'pad' })
  for (const day of days) cells.push({ kind: 'day', day })
  // Pad tail to full row of 7.
  while (cells.length % 7 !== 0) cells.push({ kind: 'pad' })
  return cells
}

function weekdayLabels(lang: 'ru' | 'en'): string[] {
  return lang === 'en'
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']
}

function stepMonth(c: { year: number; month0: number }, delta: number) {
  const d = new Date(c.year, c.month0 + delta, 1)
  return { year: d.getFullYear(), month0: d.getMonth() }
}

function dateNumber(iso: string): string {
  const [, , d] = iso.split('-')
  return d ?? iso
}

function isoLocalDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function tzGuess(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

function longDate(iso: string, lang: 'ru' | 'en'): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(y!, m! - 1, d!))
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date)
}
